/**
 * Vuex plugin for persisting state to localStorage
 * Automatically saves selection and catalog data
 * Restores on app initialization
 */

import { Store } from 'vuex';
import type { RootState } from '../types';

const STORAGE_KEY_ENTITIES = 'carenuity_entities_cache';
const STORAGE_KEY_SELECTION = 'carenuity_selection_state';
const CACHE_VERSION = '1.2'; // Updated to match catalog module - forces cache refresh for deviceAppName/gatewayAppName fix

interface StorageData {
  version: string;
  timestamp: number;
  state: any;
}

/**
 * Load data from localStorage
 */
function loadFromLocalStorage(key: string): any | null {
  try {
    const serialized = localStorage.getItem(key);
    if (!serialized) return null;

    const data = JSON.parse(serialized);

    // Check version compatibility
    if (data.version !== CACHE_VERSION) {
      console.log('[Persistence] Cache version mismatch, clearing cache');
      localStorage.removeItem(key);
      return null;
    }

    return data.state;
  } catch (error) {
    console.error('[Persistence] Error loading from localStorage:', error);
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Save data to localStorage
 */
function saveToLocalStorage(key: string, state: any): void {
  try {
    const data: StorageData = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      state: state
    };
    const serialized = JSON.stringify(data);
    localStorage.setItem(key, serialized);
  } catch (error: any) {
    console.error('[Persistence] Error saving to localStorage:', error);
    // Handle quota exceeded errors
    if (error.name === 'QuotaExceededError') {
      console.warn('[Persistence] localStorage quota exceeded, clearing old data');
      localStorage.removeItem(key);
    }
  }
}

/**
 * Check if cache is expired
 */
function isCacheExpired(lastFetched: number | undefined): boolean {
  if (!lastFetched) return true;
  const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
  return (Date.now() - lastFetched) > CACHE_TTL;
}

/**
 * Create the persistence plugin
 */
export default function createPersistencePlugin() {
  return (store: Store<RootState>) => {
    // Restore state on initialization
    const cachedEntities = loadFromLocalStorage(STORAGE_KEY_ENTITIES);
    const cachedSelection = loadFromLocalStorage(STORAGE_KEY_SELECTION);

    // Restore catalog if valid and not expired
    if (cachedEntities && !isCacheExpired(cachedEntities.lastFetched)) {
      console.log('[Persistence] Restoring catalog from cache');
      store.commit('catalog/setNormalizedData', cachedEntities);
    } else {
      console.log('[Persistence] Catalog cache expired or invalid');
    }

    // Restore selection state
    if (cachedSelection) {
      console.log('[Persistence] Restoring selection state');
      if (cachedSelection.selectedHomeId) {
        store.commit('selection/setSelectedHomeId', cachedSelection.selectedHomeId);
      }
      if (cachedSelection.selectedRoomId) {
        store.commit('selection/setSelectedRoomId', cachedSelection.selectedRoomId);
      }
      if (cachedSelection.selectedDeviceId) {
        store.commit('selection/setSelectedDeviceId', cachedSelection.selectedDeviceId);
      }
      if (cachedSelection.selectedGatewayId) {
        store.commit('selection/setSelectedGatewayId', cachedSelection.selectedGatewayId);
      }
    }

    // Subscribe to mutations to save state changes
    store.subscribe((mutation, state: RootState) => {
      // Save catalog module changes
      if (mutation.type.startsWith('catalog/')) {
        const catalogToSave = {
          homes: state.catalog.homes,
          rooms: state.catalog.rooms,
          devices: state.catalog.devices,
          gateways: state.catalog.gateways,
          relationships: state.catalog.relationships,
          photoUrlByHome: state.catalog.photoUrlByHome,
          lastFetched: state.catalog.lastFetched,
          cacheVersion: state.catalog.cacheVersion,
        };
        saveToLocalStorage(STORAGE_KEY_ENTITIES, catalogToSave);
      }

      // Save selection module changes
      if (mutation.type.startsWith('selection/')) {
        const selectionToSave = {
          selectedHomeId: state.selection.selectedHomeId,
          selectedRoomId: state.selection.selectedRoomId,
          selectedDeviceId: state.selection.selectedDeviceId,
          selectedGatewayId: state.selection.selectedGatewayId,
        };
        saveToLocalStorage(STORAGE_KEY_SELECTION, selectionToSave);
      }

      // Clear cache on logout
      if (mutation.type === 'logout' || mutation.type === 'auth/logout') {
        console.log('[Persistence] Clearing cache on logout');
        localStorage.removeItem(STORAGE_KEY_ENTITIES);
        localStorage.removeItem(STORAGE_KEY_SELECTION);
      }
    });

    // Provide a method to manually clear cache
    (store as any).clearPersistedCache = () => {
      console.log('[Persistence] Manually clearing cache');
      localStorage.removeItem(STORAGE_KEY_ENTITIES);
      localStorage.removeItem(STORAGE_KEY_SELECTION);
      store.commit('catalog/clearCache');
      store.commit('selection/reset');
    };
  };
}
